export type PrivacyPolicySection = {
  title: string;
  description?: string;
  list?: { title?: string; content: string }[];
};

export const privacyPolicySections: { data: PrivacyPolicySection[] } = {
  data: [
    {
      title: "Last Updated: 18th of December, 2024",
    },
    {
      title:
        "These Terms of Service govern all Order Forms entered into between Kearney and Customer (each as defined below).",
    },
    {
      title: "1. Definitions",
      list: [
        {
          title: "1.1. Affiliate",
          content:
            "\"Affiliate\" means, with respect to an entity, any other entity, whether incorporated or not, that is controlled by, controls or is under common control with such entity. \"Control\" means the ability, whether directly or indirectly, to direct the affairs of another by means of ownership, contract or otherwise.",
        },
        {
          title: "1.2. Agreement",
          content:
            "\"Agreement\" means, collectively: (a) these Terms of Service, (b) any Order Form, (c) any exhibits, schedules, or appendices attached thereto or incorporated by reference, and (d) any amendments and addenda to any of the foregoing.",
        },
        {
          title: "1.3. Confidential Information",
          content:
            "\"Confidential Information\" means nonpublic information that (1) Kearney or Customer designates as confidential, or (2) under the circumstances surrounding disclosure, ought to be treated as confidential. Confidential Information includes the nonpublic portions of the Services. Confidential Information does not include information that: (a) is or subsequently becomes generally available without the receiving party's breach of any obligation owed to the other party, (b) is or subsequently becomes known to the receiving party from a source other than the disclosing party and such disclosure does not result from any breach of an obligation of confidentiality owed with respect to such Confidential Information, or (c) is independently developed without reference to any Confidential Information in any form.",
        },
        {
          title: "1.4. Customer",
          content:
            "\"Customer\" means the entity that is a party to the Agreement with Kearney.",
        },
        {
          title: "1.5. Customer Material",
          content:
            "\"Customer Material\" means data, information or material that Customer or any User transfers to Kearney for processing, storage or hosting by the Services.",
        },
        {
          title: "1.6. Kearney",
          content:
            "\"Kearney\" means A.T. Kearney Limited of Adelphi, 12th Floor, 1-11 John Adam Street, London, WC2N 6HT or its Affiliate that executes an Order Form that is governed by these Terms of Service.",
        },
        {
          title: "1.7. Order Form",
          content:
            "\"Order Form\" means an order form (which may or may not be in email form), schedule, or other document that is entered into or accepted by Customer and Kearney and that sets forth the Services being provided by Kearney to Customer under these Terms of Service.",
        },
        {
          title: "1.8. Users",
          content:
            "\"Users\" means the persons designated and granted access to the Services by or on behalf of Customer, which may include persons employed by Customer's Affiliates and third-party service providers.",
        },
        {
          title: "1.9. Service(s)",
          content:
            "\"Service(s)\" means Kearney ContractIQ solution provided or made accessible to Customer in accordance with an Order Form.",
        },
      ],
    },
    {
      title: "2. Access and Use of Services",
      list: [
        {
          title: "2.1. Access and Credentials",
          content:
            "Customer and its Users may access and use the Services subject to the terms of the Agreement. Customer must establish an account for accessing the Services (\"Account\"), including a username and password to access such account (\"Credentials\"). Customer is responsible for activities that occur under its Credentials and Account. Customer agrees to notify Kearney promptly of any unauthorized use of its Credentials or Account or any other suspected breach of security relating to the Services.",
        },
        {
          title: "2.2. User Compliance",
          content:
            "Any use of the Services by Users must be solely for the benefit of Customer. Customer will be responsible for its Users' compliance with the Agreement.",
        },
        {
          title: "2.3. Software License",
          content:
            "To the extent use of a Service requires Customer to install software, Kearney grants to Customer a limited, non-transferable, non-sublicensable, non-exclusive license during the term of the Order Form to use the object code of the software internally in connection with Customer's and its Affiliates use of the Service, subject to the terms and conditions of the Agreement.",
        },
        {
          title: "2.4. Prohibited Activities",
          content:
            "Customer will not (a) sublicense or resell access to or use of the Services, (b) reverse engineer, disassemble, decompile or derive the source code from the Services, (c) access or use the Services to build a competitive product or service, (d) make derivative works based upon the Services, or (e) reproduce any portion of the Services.",
        },
        {
          title: "2.5. Service Usage Restrictions",
          content:
            "Customer will not use the Services (a) in a way prohibited by law, regulation, governmental order or decree, (b) to violate the rights of others, (c) to try to gain unauthorized access to or disrupt any service, device, data, account or network, (d) to spam or distribute malware, (e) in a way that could harm the Services or impair anyone else's use of it, or (f) in a manner inconsistent with the Documentation.",
        },
        {
          title: "2.6. Service Modifications",
          content:
            "Kearney may modify the Services by adding new features, functionality, capabilities and/or services from time to time. If in Kearney's reasonable judgment any such modification is expected to materially and adversely affect the Services, Kearney will use reasonable efforts to provide at least thirty (30) days' advance written notice to Customer of such material modification.",
        },
        {
          title: "2.7. Performance Dependencies",
          content:
            "Kearney's performance may be dependent on the Customer's performance of its responsibilities and on timely decisions and approvals by Customer. Kearney's failure to perform will be excused to the extent its non-performance is caused by Customer. Kearney will be entitled to rely upon the accuracy and completeness of information provided by, and upon the decisions and approvals of, Customer.",
        },
      ],
    },
    {
      title: "3. Order Forms",
      list: [
        {
          title: "3.1. Order Forms and Modifications",
          content:
            "Kearney and Customer will enter an Order Form to define the access and use of the Services. The Order Form will not modify these Terms of Service unless the Order Form expresses a clear intent to modify such term.",
        },
      ],
    },
    {
      title: "4. Fees",
      list: [
        {
          title: "4.1. Payment Terms",
          content:
            "Unless the Services are provided on a trial and free of charge basis as agreed to in an Order Form, Customer will pay the fees set forth in the Order Form within thirty (30) days of receipt of the invoice. All prices exclude VAT.",
        },
      ],
    },
    {
      title: "5. Intellectual Property",
      list: [
        {
          title: "5.1. Rights Retention",
          content:
            "Kearney and its suppliers retain all rights in the Services including, without limitation, all copyrights, patents, trade secrets, trademarks and any other intellectual property rights, and all copies, partial copies, adaptations, additions, collective works, compilations, derivative works, enhancements (whether or not performed at the request of Customer or paid for by Customer), modifications, and translations. The Agreement grants no ownership rights to Customer. No license express or implied is granted to Customer except as expressly provided under the Agreement. Customer and its suppliers retain all rights in Customer Material.",
        },
        {
          title: "5.2. Use of Names and Trademarks",
          content:
            "Neither party may use the other party's name or trademarks, or issue any public statement relating to the Agreement, except upon written consent of the other party with respect to each instance. However, Customer grants Kearney and its Affiliates the right to include Customer's name and logo in lists of representative customers.",
        },
        {
          title: "5.3. License Grant and Data Usage",
          content:
            "Customer grants to Kearney a nonexclusive license to use Customer Material as reasonably necessary to provide the Services, to prevent or address service or technical problems, or as may be required by law. Kearney may aggregate statistical data regarding usage of the Services. Such data will be the sole property of Kearney. Except as reasonably required for its internal business purposes, Kearney will not disclose such data in a manner that permits the identification of Customer.",
        },
      ],
    },
    {
      title: "6. Confidentiality",
      list: [
        {
          title: "6.1. Use and Protection of Confidential Information",
          content:
            "Neither party may use the other party's Confidential Information except as reasonably required for its internal business purposes. Each party will hold in confidence the other party's Confidential Information under a standard of care that is no less restrictive than the standard applied to its own confidential materials, but no less than a reasonable standard of care. Neither party may disclose the other party's Confidential Information to anyone other than a person who is bound by confidentiality obligations no less protective than those described in the Agreement and who needs to know the information to perform such party's obligations.",
        },
        {
          title: "6.2. Legal Process",
          content:
            "If a party receives legal process that demands disclosure of the other party's Confidential Information, such party will give sufficient notice to the other party to enable it to challenge the demand.",
        },
        {
          title: "6.3. Return or Destruction",
          content:
            "Upon request, each party will return or destroy (at the other party's election) all copies of the other party's Confidential Information in the party's possession or control. However, each party may retain copies of Confidential Information that are embedded in archival backups, are an integral part of its technical or business records, or if required by law.",
        },
      ],
    },
    {
      title: "7. Security",
      list: [
        {
          title: "7.1. Security Measures",
          content:
            "Without limiting Section 9.5, Kearney will implement reasonable and appropriate measures designed to help secure Customer Material against accidental or unlawful loss, access or disclosure. Customer is responsible for properly configuring and using the Services and taking appropriate action to secure, protect and backup its Customer Material, including using encryption and routine backup.",
        },
      ],
    },
    {
      title: "8. Obligations of the Parties",
      list: [
        {
          title: "8.1. Compliance with Laws",
          content:
            "Each party will comply with all applicable laws and regulations (including all applicable data protection law) in relation to its performance under the Agreement. However, Kearney is not responsible for compliance with any laws or regulations applicable to Customer or Customer's industry that are not generally applicable to Kearney's provision of services to its customers generally.",
        },
        {
          title: "8.2. Customer Material Responsibility",
          content:
            "Customer is solely responsible for the accuracy, content and legality of all Customer Material. Customer warrants that Customer has and will have sufficient rights in the Customer Material to grant the rights to Kearney under the Agreement and that the Customer Material will not violate the rights of any third party.",
        },
        {
          title: "8.3. Warranty Disclaimer",
          content:
            "EXCEPT AS EXPRESSLY STATED IN THIS AGREEMENT, THE SERVICES ARE PROVIDED ON AN \"AS IS\" AND \"AS AVAILABLE\" BASIS. THERE ARE NO OTHER WARRANTIES OR REPRESENTATIONS, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION, THOSE OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NONINFRINGEMENT, ACCURACY, OMISSIONS, COMPLETENESS, OR CURRENTNESS. KEARNEY DOES NOT WARRANT THAT THE USE OF THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE OR THAT ANY CUSTOMER MATERIAL WILL BE SECURE OR NOT OTHERWISE LOST OR ALTERED.",
        },
      ],
    },
    {
      title: "9. Indemnification, Limitation of Liability",
      list: [
        {
          title: "9.1. Customer Indemnification",
          content:
            "Customer will defend, indemnify, and hold Kearney (and its Affiliates, officers, directors, employees and agents) harmless from and against all damages, costs, losses and liabilities for any Claim arising out of or relating to the Customer Material or Customer's unauthorized use of the Services.",
        },
        {
          title: "9.2. Limitation of Liability",
          content:
            "In no event will Kearney be liable for any loss of use, lost data, interruption of business, costs of delay, lost profits, or indirect, special, incidental, exemplary or consequential loss or damages arising from or related to the Agreement (including, but not limited to, business interruption, lost business, lost profits or lost savings) whether or not Kearney has been advised of the possibility of such loss or damages. Kearney's maximum aggregate liability for all claims relating to the Agreement (whether in contract or tort, including negligence and strict liability, or by statute or otherwise) will not exceed the greater of (i) \u00a3500 and (ii) the total fees paid by Customer for the Services. Any action by Customer must be brought within two (2) years after the cause of action arose.",
        },
      ],
    },
    {
      title: "10. Term and Termination",
      list: [
        {
          title: "10.1. Term",
          content:
            "The term of the Agreement commences on the earlier of (a) Customer's initial access to any Service through any means and (b) the effective date of the Order Form governed by these Terms of Service. The Agreement will remain in effect for the term as specified in the Order Form.",
        },
        {
          title: "10.2. Termination",
          content:
            "Either party may terminate the Agreement and/or Order Form if the other party materially breaches the Agreement and such breach has not been cured within thirty (30) days of receiving notice specifying the breach. In addition to any of its other rights or remedies (including termination rights), Kearney may suspend provision of Services (a) if Customer is thirty (30) days or more overdue on a payment, (b) if Kearney deems such suspension necessary as a result of Customer's breach of the Agreement, (c) if Kearney reasonably determines suspension is necessary to avoid material harm to Kearney or its other customers, or (d) as required by law.",
        },
        {
          title: "10.3. Survival",
          content:
            "Any terms of the Agreement that expressly survive termination or expiration, or by their nature ought to survive termination or expiration, will survive, including but not limited to provisions regarding confidentiality, disclaimers, exclusions and limitation of liability, indemnification, effect of termination, controlling law and jurisdiction, notices and other provisions of interpretation and enforcement.",
        },
      ],
    },
    {
      title: "11. Miscellaneous",
      list: [
        {
          title: "11.1. Modifications to Terms",
          content:
            "Kearney may change the terms and conditions of these Terms of Service from time to time (\"Modifications\") in its discretion. Kearney will give Customer notice of the changes by posting an updated version of these Terms of Service online or by emailing Customer. Modifications will be effective as of the date Kearney posts them or otherwise notifies Customer of them, unless Kearney specifies a different effective date when it makes the Modifications. Customer is responsible for checking for updates to these Terms of Service. If Customer determines in its reasonable discretion that a Modification will have a material, adverse effect on Customer, then for a period of ten (10) business days after the date of notice to Customer (the \"Termination Window\"), Customer may, as its sole and exclusive remedy for any Modification, terminate without penalty any affected Order Form upon thirty (30) days' written notice to Kearney. By continuing to access or use the Services after such Termination Window, Customer agrees to be bound by such Modification.",
        },
        {
          title: "11.2. Notices",
          content:
            "All notices, requests, demands, and other communications provided for under the Agreement must be given in writing and in English and will be deemed to have been delivered and given for all purposes (a) on the delivery date if delivered personally to the party to whom the same is directed, (b) two (2) business days after deposit with a commercial overnight carrier, with written verification of receipt, or (c) five (5) business days after the mailing date, whether or not actually received, if sent by mail, return receipt requested, postage and charges prepaid, or any other means of rapid mail delivery for which a receipt is available. Notices to Customer may also be posted on the website for the Services or by e-mail (to the address listed in Customer's account contact information). Electronic notice will be effective as of the date of posting (for notice on the website) or as of the sent date (for e-mail notice). Physical notices to Customer will be sent to the physical address listed in Customer's account contact information. Notices to Kearney must be sent to the applicable contracting party as indicated in the Order Form.",
        },
        {
          title: "11.3. Assignment",
          content:
            "Customer will not assign or delegate any rights or obligations under the Agreement, whether by operation of law or otherwise. Any purported assignment in violation of this paragraph is void.",
        },
        {
          title: "11.4. Governing Law",
          content:
            "This Agreement and all issues or claims arising out of or in connection with this Agreement shall be governed by the laws of England and Wales and any claim arising out of or in connection this Agreement shall be subject to the English courts.",
        },
        {
          title: "11.5. Severability and Waiver",
          content:
            "If for any reason a court of competent jurisdiction finds any term of the Agreement to be unenforceable, that term will be enforced to the maximum extent permissible to give effect to the intent of the parties, and the remainder of the Agreement will continue in full force and effect. No waiver of any breach of the Agreement will constitute a waiver of any prior, concurrent or subsequent breach, and no waiver will be effective unless in writing and signed by the waiving party. No amendment to the Agreement will be binding unless in writing and signed by each party.",
        },
        {
          title: "11.6. Entire Agreement",
          content:
            "The Agreement, together with all Order Forms made part of the Agreement, comprises the entire agreement between Customer and Kearney and supersedes all prior or contemporaneous negotiations, discussions or agreements, whether written or oral, between the parties regarding the subject matter contained herein. The Order Form will not modify any term within the body of the Agreement unless the Order Form expresses a clear intent to modify such term.",
        },
      ],
    },
  ],
};
